// Neon 8-Ball Pool Client (Aim Guide, Power Slider & Physics)
import { sounds } from '../sound.js';

export class PoolGameClient {
  constructor(socket, containerEl) {
    this.socket = socket;
    this.container = containerEl;
    this.balls = [];
    this.players = [];
    this.currentTurnId = null;
    this.playerSuits = {};
    this.isMoving = false;
    this.aimAngle = 0;
    this.shotPower = 12;

    this.render();
    this.setupSocketEvents();
    this.setupControls();
  }

  render() {
    this.container.innerHTML = `
      <div class="pool-wrapper">
        <div class="pool-header">
          <div class="pool-meta">
            <h2>🎱 NEON 8-BALL POOL</h2>
            <span class="pool-sub">Aim with mouse • Adjust power slider • Sink your suit (Solids vs Stripes) and the 8-Ball!</span>
          </div>
          <div id="pool-turn-badge" class="pool-badge">Waiting...</div>
        </div>

        <div class="pool-canvas-box">
          <canvas id="pool-canvas" width="800" height="440"></canvas>
        </div>

        <!-- Power Slider & Shoot Controls -->
        <div class="pool-controls-bar">
          <div class="power-group">
            <label>⚡ SHOT POWER:</label>
            <input type="range" id="pool-power-slider" min="4" max="24" value="12" step="1" />
            <span id="pool-power-val">12</span>
          </div>
          <button id="btn-pool-shoot" class="btn-pool-shoot">STRIKE CUE BALL 🎯</button>
        </div>
      </div>
    `;

    this.canvas = document.getElementById('pool-canvas');
    this.ctx = this.canvas.getContext('2d');

    const powerSlider = document.getElementById('pool-power-slider');
    const powerVal = document.getElementById('pool-power-val');
    powerSlider?.addEventListener('input', (e) => {
      this.shotPower = parseFloat(e.target.value);
      if (powerVal) powerVal.innerText = `${this.shotPower}`;
    });

    document.getElementById('btn-pool-shoot')?.addEventListener('click', () => {
      if (this.currentTurnId === this.socket.id && !this.isMoving) {
        sounds.playPop();
        this.socket.emit('pool_shoot', { angle: this.aimAngle, power: this.shotPower });
      }
    });
  }

  setupControls() {
    const handleAim = (clientX, clientY) => {
      const rect = this.canvas.getBoundingClientRect();
      const mouseX = (clientX - rect.left) * (this.canvas.width / rect.width);
      const mouseY = (clientY - rect.top) * (this.canvas.height / rect.height);

      const cue = this.balls[0];
      if (cue && !cue.inPocket) {
        this.aimAngle = Math.atan2(mouseY - cue.y, mouseX - cue.x);
        this.draw();
      }
    };

    this.mouseMoveHandler = (e) => handleAim(e.clientX, e.clientY);
    this.touchMoveHandler = (e) => {
      if (e.touches && e.touches[0]) {
        e.preventDefault();
        handleAim(e.touches[0].clientX, e.touches[0].clientY);
      }
    };

    this.canvas.addEventListener('mousemove', this.mouseMoveHandler);
    this.canvas.addEventListener('touchmove', this.touchMoveHandler, { passive: false });
    this.canvas.addEventListener('touchstart', this.touchMoveHandler, { passive: false });
  }

  setupSocketEvents() {
    this.socket.on('pool_started', (data) => {
      this.players = data.players;
      this.currentTurnId = data.currentTurnId;
      this.balls = data.balls;
      this.playerSuits = data.playerSuits;
      sounds.playChime();
      this.updateUI();
      this.draw();
    });

    this.socket.on('pool_state', (data) => {
      this.balls = data.balls;
      this.isMoving = data.isMoving;
      this.draw();
    });

    this.socket.on('pool_suitsAssigned', (data) => {
      this.playerSuits = data.playerSuits;
      this.updateUI();
    });

    this.socket.on('pool_turnChange', (data) => {
      this.currentTurnId = data.currentTurnId;
      sounds.playTick();
      this.updateUI();
      this.draw();
    });

    this.socket.on('pool_gameOver', (data) => {
      sounds.playVictory();
    });
  }

  updateUI() {
    const badge = document.getElementById('pool-turn-badge');
    const isMe = this.currentTurnId === this.socket.id;
    const curP = this.players.find(p => p.id === this.currentTurnId);
    const mySuit = this.playerSuits[this.socket.id];

    if (badge) {
      const suitText = mySuit ? ` (Your Suit: ${mySuit.toUpperCase()})` : '';
      badge.innerText = isMe ? `👉 YOUR TURN TO SHOOT${suitText}!` : `Turn: ${curP?.name || 'Player'}`;
      badge.style.color = isMe ? '#22c55e' : '#38bdf8';
    }
  }

  draw() {
    if (!this.ctx) return;

    // Table Felt (Deep Emerald/Navy)
    this.ctx.fillStyle = '#064e3b';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    // Wood / Neon Rails
    this.ctx.strokeStyle = '#047857';
    this.ctx.lineWidth = 35;
    this.ctx.strokeRect(17.5, 17.5, this.canvas.width - 35, this.canvas.height - 35);

    // Neon Outer Border
    this.ctx.strokeStyle = '#10b981';
    this.ctx.lineWidth = 3;
    this.ctx.strokeRect(35, 35, this.canvas.width - 70, this.canvas.height - 70);

    // Pockets (6)
    const pockets = [
      { x: 35, y: 35, r: 24 }, { x: 400, y: 28, r: 22 }, { x: 765, y: 35, r: 24 },
      { x: 35, y: 405, r: 24 }, { x: 400, y: 412, r: 22 }, { x: 765, y: 405, r: 24 }
    ];
    pockets.forEach((p) => {
      this.ctx.fillStyle = '#0f172a';
      this.ctx.beginPath();
      this.ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
      this.ctx.fill();
    });

    // Cue Aim Trajectory Guide
    const cue = this.balls[0];
    if (cue && !cue.inPocket && !this.isMoving && this.currentTurnId === this.socket.id) {
      this.ctx.strokeStyle = 'rgba(255, 255, 255, 0.6)';
      this.ctx.setLineDash([6, 6]);
      this.ctx.lineWidth = 2;
      this.ctx.beginPath();
      this.ctx.moveTo(cue.x, cue.y);
      this.ctx.lineTo(cue.x + Math.cos(this.aimAngle) * 300, cue.y + Math.sin(this.aimAngle) * 300);
      this.ctx.stroke();
      this.ctx.setLineDash([]);

      // Cue Stick Graphic
      this.ctx.save();
      this.ctx.translate(cue.x, cue.y);
      this.ctx.rotate(this.aimAngle + Math.PI);
      this.ctx.fillStyle = '#d97706';
      this.ctx.fillRect(20, -3, 140, 6);
      this.ctx.fillStyle = '#ffffff';
      this.ctx.fillRect(18, -3, 6, 6);
      this.ctx.restore();
    }

    // Draw Balls
    this.balls.forEach((b) => {
      if (b.inPocket) return;

      this.ctx.fillStyle = b.color;
      this.ctx.shadowColor = b.color;
      this.ctx.shadowBlur = 8;
      this.ctx.beginPath();
      this.ctx.arc(b.x, b.y, 11, 0, Math.PI * 2);
      this.ctx.fill();
      this.ctx.shadowBlur = 0;

      // Ball Number / White Badge
      if (b.id !== 0) {
        this.ctx.fillStyle = '#ffffff';
        this.ctx.beginPath();
        this.ctx.arc(b.x, b.y, 5, 0, Math.PI * 2);
        this.ctx.fill();

        this.ctx.fillStyle = '#0f172a';
        this.ctx.font = 'bold 7px Rajdhani, sans-serif';
        this.ctx.textAlign = 'center';
        this.ctx.fillText(`${b.num}`, b.x, b.y + 2.5);
      }
    });
  }

  destroy() {
    if (this.mouseMoveHandler) this.canvas?.removeEventListener('mousemove', this.mouseMoveHandler);
    this.socket.off('pool_started');
    this.socket.off('pool_state');
    this.socket.off('pool_suitsAssigned');
    this.socket.off('pool_turnChange');
    this.socket.off('pool_gameOver');
  }
}
